import React, { useState, useEffect, useCallback } from 'react';
import './Home.css';
import StatusSelector from '../StatusSelector/StatusSelector';
import EditStatusButton from '../EditStatusButton/EditStatusButton';
import PatientDetailsModal from '../PatientDetailsModal';
import axios from 'axios';

const Home = () => {
  // Estado para los datos y paginación
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    per_page: 7,
    has_next: false,
    has_previous: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Estados para los filtros
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [statusInput, setStatusInput] = useState('');

  // Manejadores para los filtros de estado
  const handleAddStatus = () => {
    if (statusInput && !selectedStatuses.includes(statusInput)) {
      const newStatuses = [...selectedStatuses, statusInput];
      setSelectedStatuses(newStatuses);
      setStatusInput('');
      // Actualizar los datos con los nuevos filtros
      fetchData(1, newStatuses);
    }
  };

  const handleRemoveStatus = (statusToRemove) => {
    const newStatuses = selectedStatuses.filter(status => status !== statusToRemove);
    setSelectedStatuses(newStatuses);
    // Actualizar los datos con los filtros restantes
    fetchData(1, newStatuses);
  };

  // Opciones de estado para el filtro
  const statusOptions = [
    'Alto',
    'Intermedio',
    'Intermedio-Bajo',
    'Bajo',
    'Muy Bajo'
  ];
  
  // Mapeo de valores de riesgo al formato esperado por el backend
  const mapRiesgoToBackend = (riesgo) => {
    const map = {
      'Alto': 'alto',
      'Intermedio': 'intermedio',
      'Intermedio-Bajo': 'intermedioBajo',
      'Bajo': 'bajo',
      'Muy Bajo': 'muyBajo'
    };
    return map[riesgo] || riesgo.toLowerCase();
  };

  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [searchDni, setSearchDni] = useState('');
  const documentType = 'dni'; // Fixed to always be DNI
  const [showMotivaInfo, setShowMotivaInfo] = useState(false);

  // Configuración de axios para la API
  const api = axios.create({
    baseURL: '/api',
    timeout: 10000,
  });

  

  // Función para obtener los datos de la API
  const fetchData = useCallback(async (page = 1, statusFilters = []) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Obtener el token del localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No se encontró el token de autenticación');
      }
      
      // Construir los parámetros de la petición
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 13);
      params.append('sort', 'riesgo');
      params.append('order', 'desc');

      // Agregar filtros de riesgo si hay alguno seleccionado
      if (statusFilters && statusFilters.length > 0) {
        statusFilters.forEach(filter => {
          // Mapear los valores de riesgo al formato esperado por el backend
          let riesgoValue = mapRiesgoToBackend(filter);
          // Asegurarse de que el valor de riesgo sea uno de los permitidos
          const riesgosPermitidos = ['alto', 'intermedio', 'intermedioBajo', 'bajo', 'muyBajo'];
          if (riesgosPermitidos.includes(riesgoValue)) {
            params.append('riesgos', riesgoValue);
          }
        });
      }
      

      const response = await api.get(`/registros?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      

      
      setTableData(response.data.data || []);
      setPagination(response.data.pagination || {
        current_page: 1,
        total_pages: 1,
        total_records: 0,
        per_page: 13,
        has_next: false,
        has_previous: false
      });
      
      return response.data;
    } catch (err) {
      console.error('Error al obtener los datos:', err);
      setError('Error al cargar los datos. Por favor, intente nuevamente.');
      throw err; // Re-lanzar el error para manejarlo en el llamador
    } finally {
      setIsLoading(false);
    }
  }, [api]); // Solo api como dependencia

  // Cargar datos al montar el componente
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchData(1, selectedStatuses);
      } catch (err) {
        // El error ya fue manejado en fetchData
      }
    };

    loadData();
  }, [selectedStatuses]); // Solo selectedStatuses como dependencia

  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchData(newPage, selectedStatuses);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setShowMotivaInfo(false);
  };

  const handleMotivaInfo = () => {
    setShowMotivaInfo(true);
  };


  const handleStatusUpdate = async (dni, newStatus, fechaSeleccionada = '') => {
    // Refrescar los datos de la tabla para asegurar que todo esté actualizado
    try {
      const response = await api.get(`/registros?page=${pagination.current_page}&limit=13&sort=riesgo&order=desc`);
      setTableData(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error al actualizar los datos:', err);
      // En caso de error, actualizar localmente
      setTableData(prevData => 
        prevData.map(item => 
          item.documento === dni 
            ? { 
                ...item, 
                estado: newStatus,
                fechaSeleccionada: fechaSeleccionada || ''
              }
            : item
        )
      );
    }
    
    // Actualizar el ítem seleccionado si es necesario
    if (selectedItem && selectedItem.documento === dni) {
      setSelectedItem(prev => ({
        ...prev,
        estado: newStatus,
        fechaSeleccionada: fechaSeleccionada || ''
      }));
    }
  };

  const handleStatusSelect = async (newStatus, fechaSeleccionada = '') => {
    const itemToUpdate = tableData.find(item => item.id === editingItemId);
    
    // Actualizar el estado local
    setTableData(prevData => 
      prevData.map(item => 
        item.id === editingItemId 
          ? { 
              ...item, 
              estado: newStatus,
              fechaSeleccionada: fechaSeleccionada || item.fechaSeleccionada || ''
            } 
          : item
      )
    );
    
    // Cerrar el selector de estado
    setEditingStatus(null);
    
    try {
      // Actualizar el estado y la fecha en el backend
      const updateData = { 
        estado: newStatus,
        ...(fechaSeleccionada && { fechaSeleccionada })
      };
      
      await api.put(`/registros/${editingItemId}`, updateData);

      
      // Actualizar el ítem seleccionado si es necesario
      if (selectedItem && selectedItem.id === editingItemId) {
        setSelectedItem(prev => ({
          ...prev,
          estado: newStatus,
          fechaSeleccionada: fechaSeleccionada || prev.fechaSeleccionada || ''
        }));
      }
    } catch (error) {
      console.error('Error al actualizar el estado:', error);
      
      // Revertir el cambio en caso de error
      setTableData(prevData => 
        prevData.map(item => 
          item.id === editingItemId 
            ? { 
                ...item, 
                estado: itemToUpdate.estado,
                fechaSeleccionada: itemToUpdate.fechaSeleccionada || ''
              } 
            : item
        )
      );
      
      // Mostrar mensaje de error al usuario
      alert('No se pudo actualizar el estado. Por favor, intente nuevamente.');
    } finally {
      setEditingItemId(null);
    }
  };

  const closeStatusModal = () => {
    setEditingStatus(null);
    setEditingItemId(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchDni.trim()) {
      setError('Por favor ingrese un número de documento');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/registros/documento', {
        params: {
          tipo_documento: documentType.toUpperCase(),
          documento: searchDni.trim()
        }
      });
      

      
      if (response.data.data && response.data.data.length > 0) {
        // Actualizar la tabla con los resultados
        setTableData(response.data.data);
        setPagination(response.data.pagination);
      } else {
        // No se encontraron resultados
        setTableData([]);
        setPagination({
          current_page: 1,
          total_pages: 0,
          total_records: 0,
          per_page: 7,
          has_next: false,
          has_previous: false
        });
        setError('No se encontraron registros con los criterios de búsqueda');
      }
    } catch (error) {
      console.error('Error al buscar el documento:', error);
      setError('Error al realizar la búsqueda. Por favor, intente nuevamente.');
      // Restaurar la tabla completa en caso de error
      fetchData(1);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Función para limpiar la búsqueda y restaurar la tabla completa
  const handleClearSearch = () => {
    setSearchDni('');
    setError(null);
    setSelectedStatuses([]); // Limpiar también los filtros de riesgo
    fetchData(1, []);
  };

  // Renderizar paginación
  const renderPagination = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, pagination.current_page - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.total_pages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-button ${pagination.current_page === i ? 'active' : ''}`}
          disabled={isLoading}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button 
          onClick={() => handlePageChange(1)} 
          disabled={!pagination.has_previous || isLoading}
          className="pagination-nav"
        >
          «
        </button>
        <button 
          onClick={() => handlePageChange(pagination.current_page - 1)} 
          disabled={!pagination.has_previous || isLoading}
          className="pagination-nav"
        >
          ‹
        </button>
        
        {startPage > 1 && (
          <span className="pagination-ellipsis">...</span>
        )}
        
        {pages}
        
        {endPage < pagination.total_pages && (
          <span className="pagination-ellipsis">...</span>
        )}
        
        <button 
          onClick={() => handlePageChange(pagination.current_page + 1)} 
          disabled={!pagination.has_next || isLoading}
          className="pagination-nav"
        >
          ›
        </button>
        <button 
          onClick={() => handlePageChange(pagination.total_pages)} 
          disabled={!pagination.has_next || isLoading}
          className="pagination-nav"
        >
          »
        </button>
        
        <span className="pagination-info">
          Página {pagination.current_page} de {pagination.total_pages}
        </span>
      </div>
    );
  };

  return (
    <div className="home">
      <div className="search-and-filters">
          <div className="filters-container left-filters">
            <div className="status-filter-container">
            <div className="status-filter">
              <select 
                value={statusInput}
                onChange={(e) => setStatusInput(e.target.value)}
                className="status-select"
              >
                <option value="">Seleccionar Riesgo</option>
                {statusOptions.map((status, index) => (
                  <option key={index} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <button 
                type="button" 
                onClick={handleAddStatus}
                className="add-status-btn"
                disabled={!statusInput}
              >
                Agregar
              </button>
            </div>
            
            {selectedStatuses.length > 0 && (
              <div className="selected-statuses">
                {selectedStatuses.map((status, index) => (
                  <span key={index} className="status-tag">
                    {status}
                    <button 
                      onClick={() => handleRemoveStatus(status)}
                      className="remove-status"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="center-title">
          <h2>Programa Cardiometabólico</h2>
        </div>
        
        <form onSubmit={handleSearch} className="search-form right-search">
          <div className="search-container">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Buscar por DNI..."
                value={searchDni}
                onChange={(e) => setSearchDni(e.target.value)}
                className="search-input"
              />
              {searchDni && (
                <button 
                  type="button"
                  onClick={handleClearSearch}
                  className="clear-search-button"
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              )}
            </div>
            <button type="submit" className="search-button">
              Buscar
            </button>
          </div>
        </form>
      </div>
      {isLoading ? (
        <div className="loading">Cargando datos...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Apellido</th>
                  <th>Nombre</th>
                  <th>Documento</th>
                  <th>Teléfono</th>
                  <th>Estado</th>
                  <th>Fecha Seleccionada</th>
                  <th>Editar </th>
                  <th>Riesgo</th>     
                  <th>Origen</th>
                  <th>Localidad</th>
                  <th>Acciones</th>
                  
                 
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((item) => (
                    <tr key={item._id}>

                      <td>{item.apellido}</td>
                      <td>{item.nombre}</td>
                      <td>{item.documento}</td>
                      <td>{item.telefono}</td>
                      <td>
                        {editingItemId === item.id ? (
                          <StatusSelector
                            currentStatus={editingStatus}
                            onSelect={handleStatusSelect}
                            onClose={closeStatusModal}
                          />
                        ) : (
                          <span 
                            className={`status-badge ${item.estado?.toLowerCase() || ''}`}
                          >
                            {item.estado || 'Sin estado'}
                          </span>
                        )}
                      </td>
                      <td>{
                        item.fechaSeleccionada 
                          ? new Date(item.fechaSeleccionada).toLocaleDateString('es-AR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              timeZone: 'UTC' // Ensure consistent date display
                            })
                          : '-'
                      }</td>
                      <td>
                        <EditStatusButton 
                          dni={item.documento} 
                          currentStatus={item.estado} 
                          onStatusUpdate={handleStatusUpdate}
                          paciente={item}
                        />
                      </td>

                      <td>
                        {item.riesgo ? (
                          <span className={`risk-badge ${item.riesgo.toLowerCase().includes('bajo') ? 'bajo' : 
                            item.riesgo.toLowerCase().includes('medio') ? 'medio' : 
                            item.riesgo.toLowerCase().includes('alto') ? 'alto' : ''}`}>
                            {item.riesgo}
                          </span>
                        ) : 'N/A'}
                      </td>
                      <td>{item.origen_dato || '-'}</td>
                      <td>{item.localidad || '-'}</td>
                      <td>
                        <button 
                          className="details-button"
                          onClick={() => {
                            setSelectedItem(item);
                            setIsModalOpen(true);
                          }}
                        >
                          Ver Detalles
                        </button>
                      </td>
                      
  
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="no-data">
                      No se encontraron registros
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {pagination.total_pages > 1 && (
            <div className="pagination-container">
              {renderPagination()}
            </div>
          )}
        </>
      )}
      <PatientDetailsModal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        selectedItem={selectedItem} 
      />
</div>
);
};

export default Home;
